import React, { useEffect } from 'react';
import { projects } from '../data/portfolio';

const ProjectModal = ({ projectId, onClose }) => {
  const project = projects.find((p) => p.id === projectId);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = projectId ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [projectId]);

  if (!project) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)' }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${project.title} project details`}
        className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div
          className="w-full md:max-w-3xl max-h-[92vh] overflow-y-auto"
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: '2px',
          }}
        >
          {/* Header image */}
          <div className="relative" style={{ height: '240px', overflow: 'hidden' }}>
            <img
              src={project.image}
              alt={project.title}
              className="w-full h-full object-cover"
            />
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(to top, rgba(21,21,21,1) 0%, transparent 60%)' }}
            />

            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center transition-colors duration-200"
              style={{
                background: 'rgba(10,10,10,0.7)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'var(--color-text2)',
                fontSize: '18px',
                lineHeight: 1,
              }}
              aria-label="Close project modal"
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="px-6 md:px-8 pb-8 -mt-8 relative">
            {/* Category labels */}
            <div className="flex flex-wrap gap-2 mb-4">
              {project.categoryLabels.map((cat) => (
                <span
                  key={cat}
                  className="font-mono text-xs px-2 py-1"
                  style={{
                    color: 'var(--color-accent)',
                    border: '1px solid rgba(183,255,60,0.3)',
                    background: 'rgba(183,255,60,0.06)',
                  }}
                >
                  {cat}
                </span>
              ))}
            </div>

            <h2
              className="font-bold mb-4"
              style={{ fontSize: 'clamp(1.6rem, 4vw, 2.5rem)', color: 'var(--color-text)' }}
            >
              {project.title}
            </h2>

            <p
              className="text-sm md:text-base leading-relaxed mb-8"
              style={{ color: 'var(--color-text2)' }}
            >
              {project.longDescription || project.description}
            </p>

            {/* Achievements */}
            {project.achievements?.length > 0 && (
              <div className="mb-8">
                <div className="section-label mb-4">KEY OUTCOMES</div>
                <ul className="space-y-2">
                  {project.achievements.map((a, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-sm"
                      style={{ color: 'var(--color-text2)' }}
                    >
                      <span style={{ color: 'var(--color-accent)', flexShrink: 0 }}>→</span>
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tech stack */}
            <div className="mb-8">
              <div className="section-label mb-4">TECH STACK</div>
              <div className="flex flex-wrap gap-2">
                {project.technologies.map((tech) => (
                  <span
                    key={tech}
                    className="font-mono text-xs px-3 py-1.5"
                    style={{
                      color: 'var(--color-text)',
                      border: '1px solid var(--color-border)',
                      background: 'var(--color-bg)',
                    }}
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            {/* Links */}
            <div className="flex gap-4">
              {project.link && (
                <a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 font-mono text-sm px-5 py-2.5 transition-all duration-200"
                  style={{
                    background: 'var(--color-accent)',
                    color: '#0A0A0A',
                    fontWeight: 600,
                  }}
                >
                  VIEW LIVE ↗
                </a>
              )}
              {project.githubLink && (
                <a
                  href={project.githubLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 font-mono text-sm px-5 py-2.5 transition-all duration-200"
                  style={{
                    border: '1px solid var(--color-border)',
                    color: 'var(--color-text2)',
                  }}
                >
                  GITHUB ↗
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectModal;
